/* eslint-disable no-param-reassign */
import { useState, useCallback } from 'react';
import produce from 'immer';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { showNotification } from '../redux/notification';
import utils from '../utils/utils';

const INITIAL_SHIFT_GROUPS = [
	{
		date: new Date(),
		shifts: [
			{
				resourceType: null,
				shiftStructureId: null,
				unitId: null,
				unitName: '',
				numberOfProviders: 1,
				isGuaranteed: false,
				id: `id-${Math.random().toString()}`
			}
		]
	}
];

const BACKEND_FIELDS_TO_ERROR_KEY_MAP = {
	is_disabled: 'shiftStructureId',
	structure_id: 'shiftStructureId',
	slots: 'numberOfProviders'
};

const getShiftsToRequest = (shiftsGroups, isFlex) => {
	const shifts = [];
	shiftsGroups.forEach((shiftsGroup) => {
		shiftsGroup.shifts.forEach((shift) => {
			const dataObj = {
				structure_id: shift.shiftStructureId,
				slots: shift.numberOfProviders,
				unit_id: shift.unitId,
				start_date: moment(shiftsGroup.date).format('YYYY-MM-DD')
			};
			if (!isFlex) {
				dataObj.is_guaranteed = shift.isGuaranteed;
				dataObj.portalShiftId = shift.id;
			}
			shifts.push(dataObj);
		});
	});
	return shifts;
};

const createNewShift = () => {
	const newShift = { ...INITIAL_SHIFT_GROUPS[0].shifts[0] };
	newShift.id = `id-${Math.random().toString()}`;
	return newShift;
};

const computeTotalShiftsToCreate = (shiftsGroups) => {
	let count = 0;
	shiftsGroups.forEach((shiftGroup) => {
		count += shiftGroup.shifts.length;
	});
	return count;
};

const useRequestShifts = (userData, currentShiftStructures) => {
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [showFlexStructuresAlreadyStartedModal, setShowFlexStructuresAlreadyStartedModal] = useState(false);
	const [shiftsGroups, setShiftsGroups] = useState(INITIAL_SHIFT_GROUPS);
	const [errors, setErrors] = useState({});
	const dispatch = useDispatch();

	const addEmptyShiftGroup = () => {
		if (computeTotalShiftsToCreate(shiftsGroups) >= Number(userData.maxShiftsToCreate)) {
			dispatch(
				showNotification(
					'error',
					`Sorry, you can only post ${userData.maxShiftsToCreate} different shifts per each request.`
				)
			);
			return;
		}
		const nextState = produce(shiftsGroups, (draftState) => {
			const latestExistingShift = draftState[draftState.length - 1];
			draftState.push({
				date: moment(latestExistingShift.date).add(1, 'day'),
				shifts: [createNewShift()]
			});
		});
		setShiftsGroups(nextState);
	};

	const addEmptyShift = (shiftsGroupIndex) => {
		if (computeTotalShiftsToCreate(shiftsGroups) >= Number(userData.maxShiftsToCreate)) {
			dispatch(
				showNotification(
					'error',
					`Sorry, you can only post ${userData.maxShiftsToCreate} different shifts per each request.`
				)
			);
			return;
		}
		const nextState = produce(shiftsGroups, (draftState) => {
			draftState[shiftsGroupIndex].shifts.push(createNewShift());
		});
		setShiftsGroups(nextState);
	};

	const updateShiftGroupDate = (shiftsGroupIndex, newShiftGroupDate) => {
		const nextState = produce(shiftsGroups, (draftState) => {
			draftState[shiftsGroupIndex].date = newShiftGroupDate;
		});
		setShiftsGroups(nextState);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const clearErrors = (updatedObject, shiftId) => {
		const newErrors = { ...errors };
		const modifier = {};
		Object.keys(updatedObject).forEach((key) => {
			if (key === 'shiftStructureId' && updatedObject[key] === null) return;
			if (key === 'unitId' && updatedObject[key] === null) return;
			if (typeof updatedObject[key] !== 'undefined') {
				newErrors[shiftId] = {
					...newErrors[shiftId],
					[key]: !updatedObject[key],
					...modifier
				};
			}
		});
		setErrors(newErrors);
	};

	const removeShiftError = useCallback(
		(shiftId) => {
			const newErrors = produce(errors, (draft) => {
				delete draft[shiftId];
			});
			setErrors(newErrors);
		},
		[errors]
	);

	const updateShift = (shiftsGroupIndex, shiftIndex, patchObject, shiftId) => {
		const nextState = produce(shiftsGroups, (draftState) => {
			const shift = draftState[shiftsGroupIndex].shifts[shiftIndex];
			Object.keys(shift).forEach((key) => {
				if (typeof patchObject[key] !== 'undefined') {
					shift[key] = patchObject[key];
				}
			});
		});
		clearErrors(patchObject, shiftId);
		setShiftsGroups(nextState);
	};

	const removeShift = (shiftsGroupIndex, shiftIndex, shiftGeneralIndex) => {
		const nextState = produce(shiftsGroups, (draftState) => {
			const shiftsTarget = draftState[shiftsGroupIndex].shifts;
			if (shiftsTarget.length === 1) {
				draftState.splice(shiftsGroupIndex, 1);
			} else {
				shiftsTarget.splice(shiftIndex, 1);
			}
		});
		removeShiftError(shiftGeneralIndex);
		setShiftsGroups(nextState);
	};

	const checkIfStructuresAreNearToEnd = (shiftStructuresData) => {
		const structures = utils.groupDataByKey(currentShiftStructures, 'id');
		return shiftStructuresData.some((data) => {
			const [currentStructure] = structures[data.structureId];
			return utils.isStructureEndingSoon(currentStructure, userData.timezone, data.date);
		});
	};

	const checkIfStructuresAlreadyStarted = (shiftStructuresData) => {
		const structures = utils.groupDataByKey(currentShiftStructures, 'id');
		return shiftStructuresData.some((data) => {
			const [currentStructure] = structures[data.structureId];
			return utils.isStructureAlreadyStarted(currentStructure, userData.timezone, data.date);
		});
	};

	const getAllowRemove = () => {
		return (
			shiftsGroups.reduce((acc, shiftsGroup) => {
				return acc + shiftsGroup.shifts.length;
			}, 0) > 1
		);
	};

	const checkIntegrity = async (isFlex) => {
		const newErrors = {};
		const shiftStructuresIds = [];
		shiftsGroups.forEach((shiftsGroup) => {
			shiftsGroup.shifts.forEach((shift) => {
				shiftStructuresIds.push({ structureId: shift.shiftStructureId, date: shiftsGroup.date });
				if (!shift.shiftStructureId || !shift.numberOfProviders) {
					newErrors[shift.id] = {
						resourceType: !shift.resourceType,
						shiftStructureId: !shift.shiftStructureId,
						numberOfProviders: !shift.numberOfProviders
					};
				}
			});
		});
		const errorKeys = Object.keys(newErrors).length;
		if (errorKeys) {
			dispatch(showNotification('error', 'Please complete all fields.'));
		} else if (isFlex && checkIfStructuresAreNearToEnd(shiftStructuresIds)) {
			dispatch(
				showNotification(
					'error',
					'One or more of the requested shifts has ended. Please update your request and try again.'
				)
			);
		} else if (isFlex && checkIfStructuresAlreadyStarted(shiftStructuresIds)) {
			setShowFlexStructuresAlreadyStartedModal(true);
		} else {
			setShowConfirmationModal(true);
		}

		setErrors(newErrors);
	};

	const handleBackendErrors = (backendErrors) => {
		const newErrors = {};
		if (Array.isArray(backendErrors) && backendErrors.length > 0) {
			backendErrors.forEach((error) => {
				newErrors[error.portalShiftId] = {
					[BACKEND_FIELDS_TO_ERROR_KEY_MAP[error.field]]: true
				};
			});
		}

		setErrors(newErrors);
	};

	return {
		shiftsGroups,
		errors,
		getAllowRemove,
		addEmptyShiftGroup,
		addEmptyShift,
		updateShiftGroupDate,
		updateShift,
		removeShift,
		checkIfStructuresAreNearToEnd,
		checkIfStructuresAlreadyStarted,
		setShiftsGroups,
		setErrors,
		checkIntegrity,
		showConfirmationModal,
		setShowConfirmationModal,
		showFlexStructuresAlreadyStartedModal,
		setShowFlexStructuresAlreadyStartedModal,
		handleBackendErrors,
		initialShiftGroups: INITIAL_SHIFT_GROUPS,
		getShiftsToRequest
	};
};

export default useRequestShifts;
